import { CircleInfoIcon } from "@xola/icons";
import { flash, Label, SubmitButton, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import {
    FormField,
    getAddonCatalog,
    getDemographicCatalog,
    integerAtleastZero,
    requiredString,
    TextareaField,
} from "common";
import { difference, omit } from "lodash";
import pluralize from "pluralize";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    AndFilter,
    ArrivalSpanFilter,
    CollectionNode,
    EqualsFilter,
    Experience,
    InScheduleFilter,
    LineItemCode,
    LineItemTemplate,
    MinimumTransitDurationFilter,
    Package,
    PackageSequences,
    PackageType,
    Permission,
    ProductCatalogItem,
    ProductObject,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleFilterObject,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleShortcutType,
    SameDayArrivalFilter,
    Seller,
    ValidationLevel,
} from "types";
import { array, boolean, object, string } from "yup";
import { getEquipmentDisabledReasons, ProductSelector } from "../../../Common/ProductSelector";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { XolaFormik } from "../../../Common/XolaFormik";
import { InputField } from "../../../Field/InputField";
import { ContentLayout } from "../../../Layout/ContentLayout";
import { Loader } from "../../../Loader";
import { PackageFormValues, StartRestrictionType } from "../../Packages/DescriptiveContent/StartTimeRestriction";
import { getPricingTermDiscount } from "../../Packages/PackagePricingHelper";
import {
    BasicDiscountAddonTerm,
    BasicDiscountTerm,
    getCatalogByItem,
    getDefaultAction,
    getDefaultInsertAction,
    getPackageDiscountAction,
    getPackagePurchaseRuleLinkPayload,
    TermsType,
    useLineItemTemplateByCode,
} from "../../Packages/PricingSelectorHelper";
import { getProductCollectionPayload, ProductCollectionSelector } from "../../ProductCollectionSelector";
import { PartnerMinimumDurationField } from "./PartnerMinimumDurationField";
import { api } from "@/api/api";
import { humanizeEquipmentDuration } from "@/helpers/durationHelper";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useExperiences, usePartnerExperiences } from "@/hooks/resources/useProduct";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useCollection } from "@/hooks/useCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { SelectionState } from "@/hooks/useSelection";
import { useModal } from "@/hooks/useVisibility";
import { isAddonCatalogItem, isChoiceAddOn } from "@/models/CatalogItem";
import { getProductIdsInPackage } from "@/models/Package";
import {
    getExistingPurchaseRule,
    getPurchaseRulesFromPurchaseRuleLink,
    getPurchaseRulesFromPurchaseRuleLinks,
} from "@/models/PurchaseRuleLink";
import {
    createAndFilter,
    createArrivalSpanFilter,
    createEqualsFilter,
    createInScheduleFilter,
    createMinimumTransitDurationFilter,
    createSameDayArrivalFilter,
} from "@/models/RuleFilter";

type PartnerPackageFormValues = Required<Pick<PackageFormValues, "selectedPartnerExperiences">> & PackageFormValues;

// TODO: move the experiences part to a seperate validator
const validationSchema = object().shape({
    name: requiredString(),
    excerpt: requiredString(),
    desc: requiredString(),
    type: string(),
    restrictionDuration: integerAtleastZero().test({
        name: "checkStartTimeRestriction",
        message:
            "Your start time restriction should be greater than 0 and can not be less than duration between events",
        test() {
            const { minimumTransitDuration, restrictionDuration, showStartingRestriction, restrictionType } =
                this.parent;

            if (!showStartingRestriction) {
                return true;
            }

            return restrictionType === StartRestrictionType.SAME_DAY || minimumTransitDuration < restrictionDuration;
        },
    }),
    selectedExperiences: object().shape({
        all: boolean(),
        selected: array().when("all", {
            is: false,
            then: array().min(1, "are required"),
        }),
    }),
    selectedPartnerExperiences: object().shape({
        all: boolean(),
        selected: array().when("all", {
            is: false,
            then: array().min(1, "are required"),
        }),
    }),
});

// TODO: This componetn is duplicate of PacakgedescriptiveContent.tsx, need to refactor it
export const PartnerPackageDescriptiveContent = ({ pkg }: { pkg?: Package }) => {
    const history = useHistory();
    const invalidate = useInvalidate();
    const seller = useSeller();
    const collectionModal = useModal();

    const { isGranted } = useAuthorization();
    const showCollectionModification = isGranted(Permission.MODIFY_COLLECTIONS);

    const { data: addedCollections, isLoading: isLoadingAddedCollections } = useAutoFetchCollection<CollectionNode>(
        "collectionNodes",
        pkg?.id ? { product: pkg.id } : undefined,
        { enabled: !!pkg?.id && showCollectionModification },
    );
    const addedCollectionPaths = addedCollections.map((node) => node.path ?? "").filter(Boolean);

    const [selectedExperiences, setSelectedExperiences] = useState<SelectionState>({ all: false, selected: [] });

    const { data: experiences, isFetching } = useExperiences();
    const experiencesEquipmentDisabledReasons = getEquipmentDisabledReasons(experiences);

    const { data: partnerExperiences, isLoading: isLoadingPartnerExperiences } = usePartnerExperiences();
    const partnerExperiencesEquipmentDisabledReasons = getEquipmentDisabledReasons(partnerExperiences);

    const [selectedPartnerExperiences, setSelectedPartnerExperiences] = useState<SelectionState>({
        all: false,
        selected: [],
    });

    const { data: discountTemplate } = useLineItemTemplateByCode(LineItemCode.PARTNER_PACKAGE_DISCOUNT);

    const { data: purchaseRuleLinks, isLoading: isLoadingPurchaseRuleLink } = useCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        {
            "entities.id": pkg?.id,
            "entities.object": PurchaseRuleLinkEntityObject.PARTNER_PACKAGE,
            expand: "purchaseRule",
        },
        {
            enabled: !!pkg,
        },
    );

    const initialPurchaseRule: PurchaseRule[] = [
        {
            name: "",
            actions: [],
            seller: { id: seller.id },
        },
    ];

    const [purchaseRuleState, setPurchaseRuleState] = useState<PurchaseRule[]>(initialPurchaseRule);

    useEffect(() => {
        if (!pkg) return;

        const selectedProducts = getProductIdsInPackage(pkg);
        const partnerExperienceIds = new Set(partnerExperiences.map((experience) => experience.id));

        setSelectedExperiences({
            all: false,
            selected: selectedProducts.filter((id) => !partnerExperienceIds.has(id)),
        });

        setSelectedPartnerExperiences({
            all: false,
            selected: selectedProducts.filter((id) => partnerExperienceIds.has(id)),
        });
    }, [pkg, partnerExperiences]);

    useEffect(() => {
        const purchaseRules = getPurchaseRulesFromPurchaseRuleLinks(purchaseRuleLinks);
        if (purchaseRules.length > 0) {
            setPurchaseRuleState(purchaseRules);
        }
    }, [purchaseRuleLinks]);

    const transitDurationInMinutes = getMinimumDuration(purchaseRuleState[0]) ?? 0;
    const startRestriction = getStartRestriction(purchaseRuleState[0]);
    const sameDayRestriction = getSameDayRestriction(purchaseRuleState[0]);

    const initialValues: PartnerPackageFormValues = {
        name: "",
        desc: "",
        excerpt: "",
        type: PackageType.DEMOGRAPHICS_VARIES,
        seller: { id: seller.id },
        selectedExperiences,
        selectedPartnerExperiences,
        minimumTransitDuration: transitDurationInMinutes,
        showStartingRestriction: !!startRestriction || !!sameDayRestriction,
        restrictionDuration: startRestriction ?? 0,
        schedules: pkg?.schedules ?? [],
        restrictionType: sameDayRestriction ? StartRestrictionType.SAME_DAY : StartRestrictionType.WITH_IN,
        collections: addedCollectionPaths,
        ...pkg,
    };

    const { mutateAsync: savePackage } = useMutation(async (payload: Partial<PartnerPackageFormValues>) => {
        try {
            if (pkg?.id) {
                const { data: savedPackage } = await api.put<Package>(`/partnerPackages/${pkg?.id}`, payload);
                return savedPackage;
            }

            const { data: savedPackage } = await api.post<Package>("/partnerPackages", payload);
            return savedPackage;
        } catch {
            flash.danger("Error saving package");
        }
    });

    const { mutateAsync: savePurchaseRule } = useMutation(async (purchaseRuleState: PurchaseRule) => {
        if (purchaseRuleState?.id) {
            const { data: savedPurchaseRule } = await api.put<PurchaseRule>(
                `/purchaseRules/${purchaseRuleState.id}`,
                purchaseRuleState,
            );
            return savedPurchaseRule;
        }

        const { data: savedPurchaseRule } = await api.post<PurchaseRule>(`/purchaseRules`, purchaseRuleState);
        return savedPurchaseRule;
    });

    const { mutateAsync: savePurchaseRuleLink } = useMutation(async (purchaseRuleLink: PurchaseRuleLink) => {
        if (purchaseRuleLink?.id) {
            const { id } = purchaseRuleLink;
            return api.put<PurchaseRuleLink>(`/purchaseRuleLinks/${id}`, purchaseRuleLink);
        }

        return api.post<PurchaseRuleLink>(`/purchaseRuleLinks`, purchaseRuleLink);
    });

    const { mutateAsync: deletePurchaseRule } = useMutation(async (purchaseRuleState?: PurchaseRule) => {
        if (purchaseRuleState?.id) {
            await api.delete<PurchaseRule>(`/purchaseRules/${purchaseRuleState.id}`);
        }
    });

    const { mutateAsync: deletePurchaseRuleLink } = useMutation(async (purchaseRuleLink?: PurchaseRuleLink) => {
        if (purchaseRuleLink?.id) {
            return api.delete<PurchaseRuleLink>(`/purchaseRuleLinks/${purchaseRuleLink.id}`);
        }
    });

    const { mutateAsync: saveCollections } = useMutation(
        async ({ newPaths, packageId }: { newPaths: string[]; packageId: string }) => {
            const collectionUpdateOperations = getProductCollectionPayload({
                newPaths,
                oldPaths: addedCollectionPaths,
                addedCollections,
                productId: packageId,
                productObject: ProductObject.PARTNER_PACKAGE,
            });
            if (collectionUpdateOperations.length > 0) {
                await api.patch("/collectionNodes", { ops: collectionUpdateOperations });
                invalidate("collectionNodes");
            }
        },
    );

    const handleCancel = () => {
        history.push(`/products/partnerPackages`);
    };

    const handleSubmit = async (values: PartnerPackageFormValues) => {
        const savedPackage = await savePackage(getPartnerPackagePayload(values));

        if (!savedPackage?.id) {
            return null;
        }

        const selectedProductIds = pkg ? getProductIdsInPackage(pkg) : [];
        if (pkg && values.selectedProducts && values.selectedExperiences.selected !== selectedProductIds) {
            const unselectedProductIds = difference(selectedProductIds, values.selectedExperiences.selected);
            for (const productId of unselectedProductIds) {
                const purchaseRule = purchaseRuleState.find((rule) => rule.meta?.subProductId === productId);
                const purchaseRuleLink = getExistingPurchaseRule(purchaseRuleLinks, purchaseRule);
                await deletePurchaseRule(purchaseRule);
                await deletePurchaseRuleLink(purchaseRuleLink);
            }
        }

        let existingPurchaseRuleLink = null;
        if (savedPackage.selectedProducts) {
            for (const selectedProduct of savedPackage.selectedProducts) {
                const experience = experiences.find(({ id }) => selectedProduct.product.id === id);
                // TODO: use Guard condittion and return if experience is not there
                if (experience) {
                    const purchaseRule = purchaseRuleState.find((rule) => rule.meta?.subProductId === experience.id);
                    const purchaseRuleLink = getExistingPurchaseRule(purchaseRuleLinks, purchaseRule);
                    let newPurchaseRule = null;
                    if (existingPurchaseRuleLink) {
                        const discountTerm = getPricingTermDiscount(purchaseRuleState, [existingPurchaseRuleLink]);

                        if (discountTerm.termType === TermsType.BASIC && !purchaseRule) {
                            const existingRules = getPurchaseRulesFromPurchaseRuleLink(existingPurchaseRuleLink);
                            const existingRule = existingRules[0];
                            const discount = existingRule.actions[1]?.shortcut?.amount ?? 0;
                            const term = getBasicDiscountTerm(discount, discountTerm);
                            const filter = getPurchaseRuleFilter(values, savedPackage.id, experience.id);
                            newPurchaseRule = createBasicPurchaseRule(
                                savedPackage,
                                experience,
                                term,
                                filter,
                                seller,
                                discountTemplate,
                            );
                        }
                    }

                    if (purchaseRuleLink && !existingPurchaseRuleLink) {
                        existingPurchaseRuleLink = purchaseRuleLink;
                    }

                    if (!newPurchaseRule) {
                        newPurchaseRule = purchaseRule
                            ? {
                                  ...purchaseRule,
                                  filter: getPurchaseRuleFilter(values, savedPackage.id, selectedProduct.product.id),
                              }
                            : createNewRule(values, savedPackage, experience, seller, discountTemplate);
                    }

                    const savedRule = await savePurchaseRule(newPurchaseRule);

                    const purchaseRuleLinkPayload = getPackagePurchaseRuleLinkPayload(
                        seller,
                        savedPackage,
                        savedRule,
                        purchaseRuleLink,
                        undefined,
                        existingPurchaseRuleLink,
                    );

                    await savePurchaseRuleLink(purchaseRuleLinkPayload);
                    // TODO: check if the response have purchaseRules expanded and use updateCache method to save it
                    // instead of fetching it again
                    invalidate("purchaseRuleLinks", purchaseRuleLink?.id);
                }
            }

            invalidate("partnerPackages", pkg?.id);
        }

        const hasCollectionChanges =
            showCollectionModification &&
            values.collections &&
            Array.isArray(values.collections) &&
            JSON.stringify([...values.collections].sort()) !== JSON.stringify([...addedCollectionPaths].sort());

        if (hasCollectionChanges) {
            await saveCollections({ newPaths: values.collections!, packageId: savedPackage.id });
        }

        if (pkg?.id) {
            flash.success(`Successfully updated package ${pkg.name}`);
        } else {
            history.push(`/products/partnerPackages/${savedPackage.id}/pricing`);
        }
    };

    if (isLoadingPurchaseRuleLink || isFetching || isLoadingPartnerExperiences) {
        return <Loader />;
    }

    return (
        <ContentLayout isWide>
            <XolaFormik
                enablePrompt
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={initialValues}
                onSubmit={handleSubmit}
            >
                {({ values, isSubmitting, handleSubmit, setFieldValue, errors, submitCount }) => {
                    const selectedExperiencesError = submitCount > 0 && !!errors.selectedExperiences;
                    const selectedPartnerExperiencesError = submitCount > 0 && !!errors.selectedPartnerExperiences;

                    return (
                        <>
                            <h2 className="mt-5">Description</h2>
                            <div className="divide-y divide-gray-light">
                                <ContentLayout.Section
                                    title="Basic Information"
                                    description="Important details that will be presented to your customer throughout the booking process."
                                >
                                    <div className="mb-5">
                                        {showCollectionModification && (
                                            <FormField name="collections" label="">
                                                <ProductCollectionSelector
                                                    addedCollectionPaths={addedCollectionPaths}
                                                    isLoading={isLoadingAddedCollections}
                                                    value={values.collections ?? []}
                                                    isOpen={collectionModal.isVisible}
                                                    onChange={(collections: string[]) => {
                                                        setFieldValue("collections", collections);
                                                    }}
                                                    onOpenChange={(isOpen) =>
                                                        isOpen ? collectionModal.show() : collectionModal.hide()
                                                    }
                                                />
                                            </FormField>
                                        )}
                                        <FormField name="name" label="Title">
                                            <InputField isRequired />
                                        </FormField>
                                        <Label className={clsx(selectedExperiencesError && "!text-danger")}>
                                            Your Experiences* {selectedExperiencesError}
                                            <Tooltip content="Choose products that are included in this package">
                                                <CircleInfoIcon />
                                            </Tooltip>
                                        </Label>

                                        <ProductSelector
                                            hideDraft
                                            disablePerOutingPrice
                                            singleSelect
                                            hideUnScheduled={false}
                                            allowOnlyCommonDemographic={values.type === PackageType.DEMOGRAPHICS_SHARED}
                                            enableSelectAll={false}
                                            value={selectedExperiences}
                                            collections={[ProductObject.EXPERIENCE]}
                                            disabledReasons={experiencesEquipmentDisabledReasons}
                                            onChange={(selectionState) =>
                                                setFieldValue("selectedExperiences", selectionState)
                                            }
                                        />
                                    </div>

                                    <div className="mb-5">
                                        <Label className={clsx(selectedPartnerExperiencesError && "!text-danger")}>
                                            Partner Experiences* {selectedPartnerExperiencesError}
                                            <Tooltip
                                                placement="auto"
                                                content="Choose partner products that are included in this package"
                                            >
                                                <CircleInfoIcon />
                                            </Tooltip>
                                        </Label>

                                        <ProductSelector
                                            hideDraft
                                            hideUnScheduled
                                            disablePerOutingPrice
                                            singleSelect
                                            partnerExperiences
                                            allowOnlyCommonDemographic={values.type === PackageType.DEMOGRAPHICS_SHARED}
                                            enableSelectAll={false}
                                            value={selectedPartnerExperiences}
                                            collections={[ProductObject.EXPERIENCE]}
                                            disabledReasons={partnerExperiencesEquipmentDisabledReasons}
                                            onChange={(selectionState) =>
                                                setFieldValue("selectedPartnerExperiences", selectionState)
                                            }
                                        />
                                    </div>
                                    <FormField
                                        name="excerpt"
                                        label="Excerpt"
                                        tooltip="This text is shown to your customers during the online checkout process. It does not appear in the confirmation email"
                                    >
                                        <TextareaField isRequired rows={6} />
                                    </FormField>

                                    <FormField
                                        name="desc"
                                        label="Description"
                                        tooltip="This text is for participation in optional distribution channels. It does NOT appear to your customers during checkout or in the confirmation email"
                                    >
                                        <TextareaField isRequired rows={6} />
                                    </FormField>

                                    <PartnerMinimumDurationField />
                                </ContentLayout.Section>

                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton
                                        isLoading={isSubmitting}
                                        disabled={isSubmitting}
                                        onClick={handleSubmit}
                                    >
                                        {pkg ? "Save" : "Next"}
                                    </SubmitButton>

                                    <SecondaryButton type="reset" onClick={handleCancel}>
                                        Cancel
                                    </SecondaryButton>
                                </ContentLayout.Footer>

                                <ScrollToFieldError />
                            </div>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const getBasicDiscountTerm = (discount: number, discountTerm: BasicDiscountTerm): BasicDiscountTerm => {
    return {
        termType: TermsType.BASIC,
        discount: discount,
        includeAddons: discountTerm.includeAddons,
    };
};

const getPartnerPackagePayload = (values: PartnerPackageFormValues) => {
    const selectedExperiences = [];
    selectedExperiences.push(
        ...values.selectedExperiences.selected.map((experienceId, index) => ({
            product: { id: experienceId },
            sequence: (index + 1) * PackageSequences.SELECTED_PRODUCTS_SEQUENCE_START,
        })),
        ...(values.selectedPartnerExperiences.selected.map((experienceId, index) => ({
            product: { id: experienceId },
            sequence: (index + 1) * PackageSequences.SELECTED_PRODUCTS_SEQUENCE_START,
        })) || []),
    );
    return {
        ...omit(values, "selectedExperiences", "selectedPartnerExperiences", "collections", "durationUnit", "duration"),
        selectedProducts: selectedExperiences,
    };
};

const getMinimumDuration = (purchaseRule: PurchaseRule): number | null => {
    if (!purchaseRule) {
        return null;
    }

    if (purchaseRule.filter?.object !== PurchaseRuleFilterObject.AND_FILTER) {
        return null;
    }

    const minimumDurationFilters = purchaseRule.filter?.filters.filter((filter) => {
        return filter.object === "minimum_transit_duration_filter";
    }) as MinimumTransitDurationFilter[];

    return minimumDurationFilters.length > 0 ? minimumDurationFilters[0].operand : null;
};

const getStartRestriction = (purchaseRule: PurchaseRule): number | null => {
    if (!purchaseRule) {
        return null;
    }

    if (purchaseRule.filter?.object !== PurchaseRuleFilterObject.AND_FILTER) {
        return null;
    }

    const startRestrictionFilters = purchaseRule.filter?.filters.filter((filter) => {
        return filter.object === "arrival_span_filter";
    }) as ArrivalSpanFilter[];

    return startRestrictionFilters.length > 0 ? startRestrictionFilters[0].operand : null;
};

const getSameDayRestriction = (purchaseRule: PurchaseRule): SameDayArrivalFilter | null => {
    if (!purchaseRule) {
        return null;
    }

    if (purchaseRule.filter?.object !== PurchaseRuleFilterObject.AND_FILTER) {
        return null;
    }

    const sameDayRestrictionFilters = purchaseRule.filter?.filters.filter((filter) => {
        return filter.object === "same_day_arrival_filter";
    }) as SameDayArrivalFilter[];

    return sameDayRestrictionFilters.length > 0 ? sameDayRestrictionFilters[0] : null;
};

const getPurchaseRuleFilter = (values: PartnerPackageFormValues, packageId: ID, experienceId: ID): AndFilter => {
    const filters: Array<
        InScheduleFilter | EqualsFilter | MinimumTransitDurationFilter | ArrivalSpanFilter | SameDayArrivalFilter
    > = [];

    const {
        schedules,
        selectedExperiences,
        minimumTransitDuration,
        showStartingRestriction,
        restrictionDuration,
        restrictionType,
    } = values;

    if (schedules.length > 0) {
        filters.push(
            createInScheduleFilter({
                schedules: schedules,
                field: "arrivalDateTime",
                validationLevel: ValidationLevel.ERROR,
            }),
        );
    }

    if (selectedExperiences?.selected) {
        filters.push(createEqualsFilter(packageId, "parentProduct.id"), createEqualsFilter(experienceId, "product.id"));
    }

    if (minimumTransitDuration > 0) {
        const transitDuration = humanizeEquipmentDuration(minimumTransitDuration);
        const errorMessage = `Duration between all items should be greater than ${transitDuration.duration} ${pluralize(
            transitDuration.durationUnit,
            transitDuration.duration,
        )}`;
        filters.push(createMinimumTransitDurationFilter({ operand: minimumTransitDuration, errorMessage }));
    }

    if (showStartingRestriction) {
        if (restrictionType === StartRestrictionType.SAME_DAY) {
            filters.push(createSameDayArrivalFilter({}));
        } else {
            const humanizedDuration = humanizeEquipmentDuration(restrictionDuration);

            const errorMessage = `Arrival of all items should be within ${humanizedDuration.duration} ${pluralize(
                humanizedDuration.durationUnit,
                humanizedDuration.duration,
            )}`;
            filters.push(createArrivalSpanFilter({ operand: restrictionDuration, errorMessage }));
        }
    }

    return createAndFilter({ filters });
};

const createBasicPurchaseRule = (
    pkg: Package,
    experience: Experience,
    basicDiscount: BasicDiscountTerm,
    filter: AndFilter,
    seller: Seller,
    discountTemplate: LineItemTemplate | null,
) => {
    const discountPricingValues: PurchaseRuleAction[] = [];
    const catalogs = [...getDemographicCatalog(experience), ...getAddonCatalog(experience)];
    const shouldIncludeAddons = basicDiscount.includeAddons === BasicDiscountAddonTerm.INCLUDING_ADD_ONS;

    populateDiscountAction(catalogs, discountPricingValues, basicDiscount, discountTemplate);
    return {
        filter: filter,
        meta: { product: pkg.id, subProductId: experience.id, addons: shouldIncludeAddons },
        name: `Purchase rule for package ${pkg?.name} experience ${experience?.name}`,
        actions: [getDefaultInsertAction(discountTemplate), ...discountPricingValues],
        seller: { id: seller.id },
        processActionsForIgnoredValidations: true,
    };
};

const populateDiscountAction = (
    catalogs: ProductCatalogItem[],
    discountPricingValue: PurchaseRuleAction[],
    basicDiscount: BasicDiscountTerm,
    discountTemplate: LineItemTemplate | null,
) => {
    for (const catalog of catalogs) {
        const catalogItem = getCatalogByItem(catalogs, catalog) ?? catalog;
        const shouldExcludeAddons = basicDiscount.includeAddons === BasicDiscountAddonTerm.EXCLUDING_ADD_ONS;
        const discount = isAddonCatalogItem(catalogItem) && shouldExcludeAddons ? 0 : basicDiscount.discount;

        if (isChoiceAddOn(catalogItem)) {
            populateDiscountAction(
                catalog.items as ProductCatalogItem[],
                discountPricingValue,
                basicDiscount,
                discountTemplate,
            );
        } else {
            const action = getDefaultAction();
            const value = {
                shortcutType: PurchaseRuleShortcutType.PERCENT,
                discount: -1 * discount,
            };
            const discountAction = getPackageDiscountAction(value, action, catalogItem, discountTemplate);

            discountPricingValue.push(discountAction);
        }
    }
};

const createNewRule = (
    values: PartnerPackageFormValues,
    pkg: Package,
    product: Experience,
    seller: Seller,
    discountTemplate: LineItemTemplate | null,
): PurchaseRule => {
    return {
        filter: getPurchaseRuleFilter(values, pkg.id, product.id),
        meta: { product: pkg.id, subProductId: product.id },
        name: `Purchase rule for package ${pkg?.name} experience ${product?.name}`,
        actions: [getDefaultInsertAction(discountTemplate)],
        seller: { id: seller.id },
        processActionsForIgnoredValidations: true,
        shouldCheckFieldValueForIgnoredValidations: true,
    };
};
